import { Position } from '@blueprintjs/core';
import { extent, select, sum } from 'd3';
import isString from 'lodash-es/isString';
import ceil from 'lodash-es/ceil';
import numeral from 'numeral';
import { GenericObject } from 'interfaces/GenericObject';

/**
 * Returns a consistent scientific notation format for strings, ints, and doubles
 * @param value
 * @param useScientificNotation
 */
export const formatValueScientificNotation = (value) => {
	const useScientificNotation = value < 0.1;
	return useScientificNotation ? numeral(value).format('0.0e+0') : formatValue(value);
};

/**
 * Returns a consistent format for strings, ints, and doubles
 * @param value
 */
export const formatValue = (value) => {
	if (isString(value)) return value;
	if (Number.isInteger(value)) return numeral(value).format('0,0');
	return numeral(value).format('0,0.0');
};

/**
 * Hard truncates a string adding '...' or the given add
 * @param str
 * @param max
 * @param add
 * @returns {string|*}
 */
export function truncString(str, max, add) {
	add = add || '...';
	return typeof str === 'string' && str.length > max + add.length ? str.substring(0, max) + add : str;
}

/**
 *
 * @param d
 * @returns {boolean|boolean}
 */
export const isValid = (d) => {
	return !isNaN(d[0]) && !isNaN(d[1]);
};

/**
 * Transform to get back to the parents element.  This helps when the <g> has a transform applied to it but we need
 * the coordinate to use outside the svg.  For example, placing to Tooltip.
 *
 * @param element
 * @param svgDocument
 * @returns {function(*, *): {x, y}}
 */
export function makeAbsoluteContext(element, svgDocument) {
	return function (x, y) {
		const offset = svgDocument.getBoundingClientRect();
		const matrix = element.getScreenCTM();
		return {
			x: matrix.a * x + matrix.c * y + matrix.e - offset.left,
			y: matrix.b * x + matrix.d * y + matrix.f - offset.top,
		};
	};
}

/**
 * Determines the orthogonal  direction based on the given angle.
 *
 * @param angle
 * @returns {string}
 */
export function determinePositionForAngle(angle: number = 0): Position {
	let position: Position = Position.TOP;

	if (angle > Math.PI / 4 && angle < (3 * Math.PI) / 4) position = Position.RIGHT;
	if (angle > (3 * Math.PI) / 4 && angle < (5 * Math.PI) / 4) position = Position.BOTTOM;
	else if (angle > (5 * Math.PI) / 4 && angle < (7 * Math.PI) / 4) position = Position.LEFT;

	return position;
}

/**
 * Determines a reasonable choice for the next increment to show.
 *
 * This helps when you want to have some padding on the max extent of chart values.
 *
 * @param {number} value
 * @returns {number}
 */
export function nextIncrement(value: number): number {
	const increment = order(value);

	if (increment === 1) {
		return 10;
	} else if (increment === 0.1) {
		return 0.9;
	} else if (increment === 0.01) {
		return 0.09;
	} else if (value < 1 && increment < 0.01 && increment > 0) {
		//Using .01 for this case (eg increment = .0001) will work well with d3's custom axis creation.
		return 0.01 + value;
	} else if (value < 50) {
		return value % 2 === 0 ? value + 2 : value + 3;
	} else if (increment === 10 || increment === 100) {
		return ceil(value, -1);
	}

	const digits = increment.toString().length;
	return ceil(value, -(digits - 2));
}

/**
 * Determines the order of magnitude of the given value.
 *
 * @param {number} value
 * @returns {number}
 */
export function order(value: number): number {
	const order = Math.floor(Math.log(value) / Math.LN10 + 0.000000001); // because float math sucks like that
	return Math.pow(10, order);
}

export interface PropertyHierarchyData {
	count: number;
	fullSize: number;
	pageSize: number;
	value: number;
	entries: KeyCountValue[];
	keys: string[][];
}

export interface KeyCountValue {
	key: string;
	count: number;
	value: number;
	metadata?: GenericObject<string>;
	children?: KeyCountValue[];
}

export interface Stacks {
	properties: string[];
	keys: string[];
	extent: [number, number];
	rolled: KeyValuePair[];
	data: LabelValue[];
	colorPropertyMap?: Record<string, string | number>;
}

export interface KeyValuePair {
	key: string;
	value: number;
}

export interface LabelValue {
	label: string;
	value: number;
	color?: string | number;
}

/**
 * For use in stacked layouts
 *
 * need as {label:value, key1: value, key2: value}
 * @param {PropertyHierarchyData} data
 * @returns {Stacks}
 */
export function convertToStacks(data: PropertyHierarchyData): Stacks {
	const keySet = new Set();

	const entries = data.entries.map((entry) => {
		const e = {
			label: entry.key,
			value: entry.value,
		};
		if(entry.hasOwnProperty("metadata")) {
			//@ts-ignore
			e.metadata = entry.metadata;
		}

		if (entry.children) {
			entry.children.forEach((child) => {
				e[child.key] = child.value;
				keySet.add(child.key);
			});
		} else {
			e[e.label] = e.value;
			keySet.add(e.label);
		}
		return e;
	});

	const properties = data.entries.map((d) => d.key);
	const keys = Array.isArray(data.keys[1]) ? data.keys[1] : data.keys[0];

	// sum up each of the keys in the data to give the totals for each key (for the legend)
	let rolled = keys.map((key) => {
		return {
			key: key,
			value: sum(entries, (d) => d[key]?.value ?? 0),
		};
	});

	return {
		data: entries,
		properties,
		keys,
		extent: extent(entries, (d) => d.value),
		rolled,
	};
}

/**
 * Possible aggregation values
 *
 * @type {({label: string, value: string}|{label: string, value: string})[]}
 */
export const AGGREGATION_METHOD_VALUES = [
	{
		label: 'Default',
		value: 'default',
	},
	{
		label: 'Sum',
		value: 'sum',
	},
	{
		label: 'Average',
		value: 'average',
	},
];

/**
 * Possible sort values
 *
 * @type {({label: string, value: string}|{label: string, value: string})[]}
 */
export const SORT_VALUES = [
	{
		label: 'Default',
		value: 'default',
	},
	{
		label: 'Count',
		value: 'count',
	},
	{
		label: 'Alphabetical',
		value: 'alphabetical',
	},
	{
		label: 'Computed Value',
		value: 'value',
	},
	{
		label: 'Ordinal',
		value: 'ordinal',
	},
];

/**
 * Possible Orientation Values
 * @type {({label: string, value: string}|{label: string, value: string}|{label: string, value: string})[]}
 */
export const ORIENTATION_VALUES = [
	{
		label: 'Horizontal',
		value: 'horizontal',
	},
	{
		label: 'Vertical',
		value: 'vertical',
	},
	{
		label: 'Auto',
		value: 'auto',
	},
];

export const SORT_DIRECTIONS = [
	{
		label: 'Ascending',
		value: 'ascending',
	},
	{
		label: 'Descending',
		value: 'descending',
	},
];

/**
 * The hook for changing widget configs uses JSON.parse/stringify
 * so the value cannot be a function and needs to be looked up later
 * instead.
 */
export const SCALING_FUNCTIONS = [
	{
		value:"noScale",
		label: "None",
		f:(v:number) => v,
	},
	{
		value:"log",
		label: "Logarithm",
		f:Math.log,
	},
	{
		value:"sqrt",
		label: "Square Root",
		f:Math.sqrt,
	},
]

export const FONT_FAMILIES = [
	{
		label:"Times",
		value:"Times"
	},
	{
		label:"Arial",
		value:"Arial"
	},
	{
		label:"Helvetica",
		value:"Helvetica"
	},
	{
		label:"American Typewriter",
		value:"American Typewriter"
	},
	{
		label:"Andale Mono",
		value:"Andale Mono"
	},
	{
		label:"Courier New",
		value:"Courier New"
	},
	{
		label:"Monospace",
		value:"monospace"
	},
	{
		label:"Apple Chancery",
		value:"Apple Chancery"
	},
	{
		label:"Snell Roundhand",
		value:"Snell Roundhand"
	},
	{
		label:"Impact",
		value:"Impact"
	},
	
]

export const NO_DATA_MESSAGE = "Unable to find data with the current parameters. Could you make another selection?"



const canvas = document.createElement('canvas');
const context = canvas.getContext('2d');
//This function checks if a word is longer than supplied width, and shrinks it till it fits
//https://stackoverflow.com/a/27723752/
//The original solution called getComputedTextLength which turned out to be very slow when called a lot (which was the case)
// use canvas to measure seems to be faster
// https://stackoverflow.com/a/35373030/
export function fitLabelWidth(fontSize: number, fontFace: string, checkWidth: (textLength: number) => boolean): () => void {
	return function(): void {
		let self = select(this);
		let text = self.text();
		context.font = fontSize + 'px ' + fontFace;
		let textLength = context.measureText(text).width;
		//here we just want to see if the text is longer than the supplied width, so setting maxText to a high number is ok.
		while (checkWidth(textLength) && text.length > 0) {
			text = text.slice(0, -1);
			textLength = context.measureText(text + '...').width;
		}
		if(self.text() !== text) //for case when no label fits in the space, no need for ellipses
			self.text(text + '...');
	};
}
